import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Users,
  Ticket,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Mic,
  Building2,
  List,
  FileText,
  DollarSign,
  ExternalLink,
  Banknote,
} from "lucide-react";
import { toast } from "react-toastify";
import eventService from "../../../../api/EventService";
import organizerService from "../../../../api/OrganizerService";
import { formatPrice, formatPriceRange, getTicketCurrency } from "../../../../utils/currency";
import {
  RescheduleEventModal,
  CancelEventModal,
  DeleteEventModal,
  MarkAsClosedModal,
  EventActionsMenu,
} from "./components";

const EventDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { event_slug } = useParams();
  const [eventData, setEventData] = useState(location.state?.eventData || null);
  const [organizer, setOrganizer] = useState(null);
  const [isLoading, setIsLoading] = useState(!location.state?.eventData);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isMarkAsClosedModalOpen, setIsMarkAsClosedModalOpen] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);

  useEffect(() => {
    const fetchEventData = async () => {
      if (location.state?.eventData) {
        const event = location.state.eventData;
        setEventData(event);

        if (event.organizer_slug) {
          try {
            const organizersResponse = await organizerService.getOrganizers();
            if (organizersResponse.data?.status_code === 200) {
              const organizersList = Array.isArray(organizersResponse.data.data)
                ? organizersResponse.data.data
                : organizersResponse.data.data || [];
              const foundOrganizer = organizersList.find(
                (org) => org.slug === event.organizer_slug
              );
              setOrganizer(foundOrganizer);
            }
          } catch (orgError) {
            console.warn("Could not fetch organizer:", orgError);
          }
        }
        setIsLoading(false);
        return;
      }

      if (!event_slug) {
        setError("Event identifier is missing");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const eventResponse = await eventService.getEvent(event_slug);

        if (!eventResponse.status || !eventResponse.data) {
          throw new Error(eventResponse.message || "Failed to fetch event");
        }

        let event = null;
        if (eventResponse.data.status_code === 200 && eventResponse.data.data) {
          event = eventResponse.data.data;
        } else if (eventResponse.data && !eventResponse.data.status_code) {
          event = eventResponse.data;
        }

        if (!event) {
          throw new Error("Event data not found");
        }

        setEventData(event);

        if (event.organizer_slug) {
          try {
            const organizersResponse = await organizerService.getOrganizers();
            if (organizersResponse.data?.status_code === 200) {
              const organizersList = Array.isArray(organizersResponse.data.data)
                ? organizersResponse.data.data
                : organizersResponse.data.data || [];
              const foundOrganizer = organizersList.find(
                (org) => org.slug === event.organizer_slug
              );
              setOrganizer(foundOrganizer);
            }
          } catch (orgError) {
            console.warn("Could not fetch organizer:", orgError);
          }
        }
      } catch (err) {
        const errorMessage =
          err.message || "An error occurred while loading event data";
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEventData();
  }, [event_slug, location.state]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      return `${days[date.getDay()]}, ${date.getDate()} ${
        months[date.getMonth()]
      }, ${date.getFullYear()}`;
    } catch {
      return dateString;
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    try {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "";
      }
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch {
      return "";
    }
  };

  // Calculate days until event
  const getDaysUntilEvent = (dateString) => {
    if (!dateString) return null;
    try {
      const eventDate = new Date(dateString);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      eventDate.setHours(0, 0, 0, 0);
      const diffTime = eventDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return null;
    }
  };

  const getEventStatus = () => {
    const eventState = eventData?.event_state || eventData?.status;
    if (!eventState) {
      const daysUntil = getDaysUntilEvent(eventData?.event_date);
      if (daysUntil === null) return { label: "Scheduled", color: "bg-slate-100 text-slate-700" };
      if (daysUntil < 0) return { label: "Past", color: "bg-slate-100 text-slate-700" };
      if (daysUntil === 0) return { label: "Today", color: "bg-blue-100 text-blue-700" };
      return { label: "Upcoming", color: "bg-blue-100 text-blue-700" };
    }
    
    const stateLower = eventState.toLowerCase();
    switch (stateLower) {
      case "active":
        return { label: "Active", color: "bg-green-100 text-green-700" };
      case "closed":
        return { label: "Closed", color: "bg-slate-100 text-slate-700" };
      case "cancelled":
        return { label: "Cancelled", color: "bg-red-100 text-red-700" };
      default:
        return { 
          label: stateLower.charAt(0).toUpperCase() + stateLower.slice(1), 
          color: "bg-slate-100 text-slate-700" 
        };
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "N/A";
    try {
      const parts = timeString.split(":");
      if (parts.length >= 2) {
        const hours = parseInt(parts[0]);
        const minutes = parts[1];
        const period = hours >= 12 ? "PM" : "AM";
        const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
        return `${displayHours}:${minutes} ${period}`;
      }
      return timeString;
    } catch {
      return timeString;
    }
  };

  const getTicketPriceRange = () => {
    if (!eventData?.ticket_types || eventData.ticket_types.length === 0) {
      return "N/A";
    }
    const prices = eventData.ticket_types.map((t) => parseFloat(t.price));
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    
    // Get currency from first ticket (assuming all tickets use same currency)
    // Default to GHS for backward compatibility
    const currency = getTicketCurrency(eventData.ticket_types[0] || {});
    
    return formatPriceRange(min, max, currency);
  };

  const getTicketsSold = () => {
    if (!eventData?.overall_slots) return "0 tickets sold";
    const totalSlots = parseInt(eventData.overall_slots);
    return `0 out of ${totalSlots} tickets sold`;
  };

  const getAttendeesCount = () => {
    return "0 Attendees";
  };

  const getAllImages = () => {
    const images = [];
    if (eventData?.thumbnail) images.push(eventData.thumbnail);
    if (
      eventData?.featured_images &&
      Array.isArray(eventData.featured_images)
    ) {
      images.push(...eventData.featured_images);
    }
    return images;
  };

  const images = getAllImages();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-12 h-12 mx-auto mb-4 text-purple-600 animate-spin" />
          <p className="text-gray-600">Loading event details...</p>
        </div>
      </div>
    );
  }

  if (error || !eventData) {
    return (
      <div className="px-6 py-4">
        <button
          onClick={() => navigate("/admin/media/events")}
          className="flex items-center mb-6 text-gray-600 transition-colors hover:text-gray-900 group"
        >
          <ArrowLeft className="mr-2 w-4 h-4 transition-transform group-hover:-translate-x-1" />
          <span className="text-sm font-medium">Back to Events</span>
        </button>

        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <div className="p-4 mb-4 bg-red-100 rounded-full">
            <AlertCircle className="w-12 h-12 text-red-600" />
          </div>
          <h2 className="mb-2 text-xl font-bold text-gray-900">
            Error Loading Event
          </h2>
          <p className="mb-6 text-gray-600">{error || "Event not found"}</p>
          <button
            onClick={() => navigate("/admin/media/events")}
            className="px-6 py-2 text-white bg-purple-600 rounded-lg transition-colors hover:bg-purple-700"
          >
            Go Back to Events
          </button>
        </div>
      </div>
    );
  }

  const daysUntil = getDaysUntilEvent(eventData.event_date);
  const isUpcoming = daysUntil !== null && daysUntil > 0;
  const isPast = daysUntil !== null && daysUntil < 0;

  const isActionDisabled = (action) => {
    const eventState = eventData.event_state || eventData.status;
    if (!eventState) return false;

    const stateLower = eventState.toLowerCase();

    switch (action) {
      case "cancel":
        return stateLower === "cancelled" || stateLower === "closed";
      case "reschedule":
        return stateLower === "cancelled" || stateLower === "closed";
      case "edit":
        return stateLower === "cancelled" || stateLower === "closed";
      case "markAsClosed":
        return stateLower === "closed" || stateLower === "cancelled";
      default:
        return false;
    }
  };

  const eventStatus = getEventStatus();

  return (
    <div className="px-6 py-4 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate("/admin/media/events")}
          className="flex items-center px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg transition-all hover:bg-slate-50 hover:border-slate-300"
        >
          <ArrowLeft className="mr-2 w-4 h-4" />
          Back
        </button>
        <div className="flex items-center space-x-3">
          <EventActionsMenu
            onViewAttendees={() =>
              navigate(`/admin/media/events/${event_slug}/attendees`,{
                state:{event:eventData}
              })
            }
            onViewAnalytics={() =>
              navigate(`/admin/media/events/${event_slug}/analytics`, {
                state: { 
                  eventData: eventData,
                  from: 'event-details'
                },
              })
            }
            onCancel={
              isActionDisabled("cancel")
                ? undefined
                : () => setIsCancelModalOpen(true)
            }
            onReschedule={
              isActionDisabled("reschedule")
                ? undefined
                : () => {
                    setIsRescheduleModalOpen(true);
                  }
            }
            onEdit={
              isActionDisabled("edit")
                ? undefined
                : () =>
                    navigate(`/admin/media/events/edit/${event_slug}`, {
                      state: { eventData: eventData },
                    })
            }
            onDelete={() => setIsDeleteModalOpen(true)}
            onMarkAsClosed={
              isActionDisabled("markAsClosed")
                ? undefined
                : () => setIsMarkAsClosedModalOpen(true)
            }
            eventName={eventData.event_title || eventData.venue_name}
          />
        </div>
      </div>

      {/* Event Overview */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="grid grid-cols-1 gap-6 p-6 lg:grid-cols-2 lg:items-start">
          {/* Event Image */}
          <div className="relative lg:sticky lg:top-6 lg:self-start">
            {images.length > 0 ? (
              <div className="relative overflow-hidden bg-slate-100 rounded-lg w-full aspect-video shadow-md">
                <img
                  src={images[currentImageIndex]}
                  alt={eventData.venue_name || "Event"}
                  className="object-cover w-full h-full"
                />
                {images.length > 1 && (
                  <>
                    <button
                      onClick={() =>
                        setCurrentImageIndex(
                          (prev) => (prev - 1 + images.length) % images.length
                        )
                      }
                      className="absolute left-2 top-1/2 p-2 text-white bg-slate-900 bg-opacity-60 rounded-full transition-all hover:bg-opacity-80 -translate-y-1/2 backdrop-blur-sm"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() =>
                        setCurrentImageIndex(
                          (prev) => (prev + 1) % images.length
                        )
                      }
                      className="absolute right-2 top-1/2 p-2 text-white bg-slate-900 bg-opacity-60 rounded-full transition-all hover:bg-opacity-80 -translate-y-1/2 backdrop-blur-sm"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    <div className="absolute bottom-4 left-1/2 flex space-x-2 -translate-x-1/2">
                      {images.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentImageIndex(idx)}
                          className={`h-2 rounded-full transition-all ${
                            idx === currentImageIndex
                              ? "bg-white w-6"
                              : "bg-white bg-opacity-50 w-2"
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center bg-slate-100 rounded-lg aspect-video border border-slate-200">
                <Calendar className="w-16 h-16 text-slate-400" />
              </div>
            )}
          </div>

          <div className="flex flex-col space-y-4">
            <div>
              {eventData.event_title && (
                <p className="mb-1 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Event Title
                </p>
              )}
              <h1 className="mb-3 text-2xl font-bold text-slate-900 leading-tight sm:text-3xl">
                {eventData.event_title || eventData.venue_name || "Event Name"}
              </h1>
              {eventData.event_type && Array.isArray(eventData.event_type) && eventData.event_type.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {eventData.event_type.map((type, idx) => (
                    <span
                      key={idx}
                      className="px-2.5 py-1 text-xs font-medium text-slate-700 bg-slate-100 rounded-md capitalize"
                    >
                      {type.replace(/_/g, " ")}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-3">
                {organizer && (
                  <div className="flex items-center space-x-2 px-3 py-1.5 bg-slate-50 rounded-lg border border-slate-200">
                    <Building2 className="w-4 h-4 text-slate-600" />
                    <p className="text-sm text-slate-700">
                      Organized by{" "}
                      <span className="font-semibold text-slate-900">
                        {organizer.name}
                      </span>
                    </p>
                  </div>
                )}
                <div className={`px-3 py-1.5 rounded-lg ${eventStatus.color} border`}>
                  <span className="text-xs font-semibold">{eventStatus.label}</span>
                </div>
              </div>
            </div>

            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center space-x-2 mb-3">
                <div className="p-1.5 bg-slate-900 rounded-lg">
                  <Calendar className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-sm font-semibold text-slate-900">
                  Date & Time
                </h3>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-slate-600" />
                  <span className="text-sm font-medium text-slate-900">
                    Start: {formatDate(eventData.event_start_date || eventData.event_date)}
                  </span>
                  {isUpcoming && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-semibold text-blue-700 bg-blue-100 rounded-full">
                      {daysUntil} days
                    </span>
                  )}
                  {isPast && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-semibold text-slate-700 bg-slate-200 rounded-full">
                      Past
                    </span>
                  )}
                </div>
                {(eventData.event_end_date || eventData.event_date) && (eventData.event_end_date || eventData.event_date) !== (eventData.event_start_date || eventData.event_date) && (
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-900">
                      End: {formatDate(eventData.event_end_date || eventData.event_date)}
                    </span>
                  </div>
                )}
                {eventData.event_time && (
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4 text-slate-600" />
                    <span className="text-sm text-slate-700">
                      {formatTime(eventData.event_time)}
                    </span>
                  </div>
                )}
                {(eventData.initial_event_date || eventData.initial_event_time) && (
                  <div className="pt-2 mt-2 border-t border-slate-200">
                    <p className="text-xs text-slate-500 mb-1">Originally scheduled:</p>
                    {eventData.initial_event_date && (
                      <p className="text-xs text-slate-600">
                        {formatDate(eventData.initial_event_date)}
                        {eventData.initial_event_time && ` at ${formatTime(eventData.initial_event_time)}`}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Venue and Stats Section - Full Width */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Venue Card */}
        <div className="lg:col-span-2 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center space-x-2 mb-3">
            <div className="p-1.5 bg-slate-900 rounded-lg">
              <MapPin className="w-4 h-4 text-white" />
            </div>
            <h3 className="text-sm font-semibold text-slate-900">Venue</h3>
          </div>
          <div>
            <p className="mb-1.5 text-sm font-semibold text-slate-900">
              {eventData.venue_name || "Venue Name"}
            </p>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">
              {eventData.venue_street_address || ""}
              {eventData.venue_street_address &&
                (eventData.city || eventData.country) &&
                ", "}
              {eventData.city}
              {eventData.city && eventData.country && ", "}
              {eventData.country}
            </p>
            {eventData.venue_google_location && (
              <a
                href={eventData.venue_google_location}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center space-x-2 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm hover:shadow-md"
              >
                <MapPin className="w-4 h-4" />
                <span>View on Google Maps</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-1">
          {/* Attendees */}
          <div className="p-3 bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center space-x-2 mb-2">
              <Users className="w-4 h-4 text-slate-600" />
              <span className="text-xs font-medium text-slate-600">
                Attendees
              </span>
            </div>
            <p className="text-base font-bold text-slate-900">
              {getAttendeesCount()}
            </p>
            {eventData.overall_slots && (
              <p className="text-xs text-slate-500 mt-1">
                {eventData.overall_slots} total slots
              </p>
            )}
          </div>

          {/* Tickets */}
          <div className="p-3 bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center space-x-2 mb-2">
              <Ticket className="w-4 h-4 text-slate-600" />
              <span className="text-xs font-medium text-slate-600">
                Pricing
              </span>
            </div>
            <p className="text-base font-bold text-slate-900">
              {getTicketPriceRange()}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {getTicketsSold()}
            </p>
          </div>
        </div>
      </div>

      {/* About Event */}
      {eventData.about_event && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center space-x-3">
              <div className="p-2.5 bg-slate-900 rounded-lg">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">About Event</h2>
                <p className="text-sm text-slate-600">Event description and details</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
              {eventData.about_event}
            </p>
          </div>
        </div>
      )}

      {/* Performers Section */}
      {eventData.performers &&
        Array.isArray(eventData.performers) &&
        eventData.performers.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center space-x-3">
                <div className="p-2.5 bg-slate-900 rounded-lg">
                  <Mic className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Performers</h2>
                  <p className="text-sm text-slate-600">Featured artists and performers</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {eventData.performers.map((performer, idx) => {
                  const name = typeof performer === "string" ? performer : performer?.name || "Performer";
                  const image = typeof performer === "object" && performer?.image ? performer.image : null;
                  return (
                    <div
                      key={idx}
                      className="p-4 bg-cyan-50 rounded-lg border border-cyan-200"
                    >
                      <div className="flex items-center space-x-3">
                        {image ? (
                          <img src={image} alt={name} className="flex-shrink-0 w-10 h-10 rounded-lg object-cover" />
                        ) : (
                          <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 bg-slate-900 rounded-lg">
                            <Mic className="w-5 h-5 text-white" />
                          </div>
                        )}
                        <p className="text-base font-semibold text-slate-900">{name}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

      {/* Sponsors Section */}
      {eventData.sponsors &&
        Array.isArray(eventData.sponsors) &&
        eventData.sponsors.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center space-x-3">
                <div className="p-2.5 bg-slate-900 rounded-lg">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Sponsors</h2>
                  <p className="text-sm text-slate-600">Event sponsors and partners</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {eventData.sponsors.map((sponsor, idx) => (
                  <div
                    key={idx}
                    className="p-4 bg-cyan-50 rounded-lg border border-cyan-200 text-center hover:shadow-md transition-all"
                  >
                    {sponsor?.logo ? (
                      <img
                        src={sponsor.logo}
                        alt={sponsor?.name || `Sponsor ${idx + 1}`}
                        className="w-full h-20 object-contain mb-2"
                      />
                    ) : (
                      <div className="w-full h-20 flex items-center justify-center mb-2 bg-white rounded border border-cyan-200">
                        <Building2 className="w-8 h-8 text-cyan-400" />
                      </div>
                    )}
                    {sponsor?.name && (
                      <p className="text-sm font-semibold text-slate-900">
                        {sponsor.name}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

      {/* Agenda Section */}
      {eventData.agenda &&
        Array.isArray(eventData.agenda) &&
        eventData.agenda.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center space-x-3">
                <div className="p-2.5 bg-slate-900 rounded-lg">
                  <List className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Event Agenda</h2>
                  <p className="text-sm text-slate-600">Schedule of events and activities</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {eventData.agenda.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-start space-x-4 p-4 bg-cyan-50 rounded-lg border border-cyan-200"
                  >
                    <div className="flex-shrink-0 flex flex-col items-center">
                      <div className="p-2 bg-cyan-100 rounded-lg">
                        <Clock className="w-4 h-4 text-cyan-600" />
                      </div>
                      <div className="mt-2 text-xs font-semibold text-slate-700">
                        {item.time || "N/A"}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-bold text-slate-900 mb-1">
                        {item.title || "Activity"}
                      </h3>
                      {item.description && (
                        <p className="text-sm text-slate-700 leading-relaxed">
                          {item.description}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

      {/* Ticket Types Section */}
      {eventData.ticket_types &&
        Array.isArray(eventData.ticket_types) &&
        eventData.ticket_types.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center space-x-3">
                <div className="p-2.5 bg-slate-900 rounded-lg">
                  <Ticket className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Ticket Types</h2>
                  <p className="text-sm text-slate-600">Available ticket types and pricing</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {eventData.ticket_types.map((ticket, idx) => {
                  return (
                    <div
                      key={idx}
                      className="p-5 bg-cyan-50 rounded-lg border border-cyan-200 hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-slate-900 rounded-lg">
                            <Ticket className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <h3 className="text-base font-bold text-slate-900">
                              {ticket.name || "Ticket Type"}
                            </h3>
                            <div className="flex items-center space-x-1 mt-1">
                              <Banknote className="w-4 h-4 text-cyan-600" />
                              <span className="text-lg font-bold text-slate-900">
                                {formatPrice(ticket.price || 0, getTicketCurrency(ticket))}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      {ticket.description && (
                        <p className="text-sm text-slate-700 mb-3 leading-relaxed">
                          {ticket.description}
                        </p>
                      )}
                      {ticket.whats_included &&
                        Array.isArray(ticket.whats_included) &&
                        ticket.whats_included.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-semibold text-slate-700 mb-2">
                              What's Included:
                            </p>
                            <div className="space-y-1.5">
                              {ticket.whats_included.map((item, itemIdx) => (
                                <div
                                  key={itemIdx}
                                  className="flex items-center space-x-2 text-xs text-slate-700"
                                >
                                  <CheckCircle className="w-3.5 h-3.5 text-cyan-600" />
                                  <span>{item}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

      {/* Modals */}
      <RescheduleEventModal
        isOpen={isRescheduleModalOpen}
        onClose={() => setIsRescheduleModalOpen(false)}
        onConfirm={async (data) => {
          setIsActionLoading(true);
          try {
            // Format payload to match API requirements
            // data.date is already in YYYY-MM-DD format from date input
            // data.time is already in HH:MM format from time input
            const payload = {
              event_date: data.date, // Format: YYYY-MM-DD
              event_time: data.time, // Format: HH:MM
            };

            const response = await eventService.rescheduleEvent(
              event_slug,
              payload
            );
            if (response.status) {
              toast.success(
                response.message || "Event rescheduled successfully"
              );
              setIsRescheduleModalOpen(false);
              // Refresh event data
              const eventResponse = await eventService.getEvent(event_slug);
              if (eventResponse.status && eventResponse.data) {
                let event = null;
                if (
                  eventResponse.data.status_code === 200 &&
                  eventResponse.data.data
                ) {
                  event = eventResponse.data.data;
                } else if (
                  eventResponse.data &&
                  !eventResponse.data.status_code
                ) {
                  event = eventResponse.data;
                }
                if (event) setEventData(event);
              }
            } else {
              toast.error(response.message || "Failed to reschedule event");
            }
          } catch (err) {
            toast.error(err.message || "An error occurred");
          } finally {
            setIsActionLoading(false);
          }
        }}
        currentDate={formatDateForInput(eventData.event_date)}
        currentTime={eventData.event_time || ""}
        eventName={eventData.event_title || eventData.venue_name}
        isLoading={isActionLoading}
      />

      <CancelEventModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={async () => {
          setIsActionLoading(true);
          try {
            const response = await eventService.cancelEvent(event_slug, {});
            if (response.status) {
              toast.success(response.message || "Event cancelled successfully");
              setIsCancelModalOpen(false);
              navigate("/admin/media/events");
            } else {
              toast.error(response.message || "Failed to cancel event");
            }
          } catch (err) {
            toast.error(err.message || "An error occurred");
          } finally {
            setIsActionLoading(false);
          }
        }}
        eventName={eventData.event_title || eventData.venue_name}
        isLoading={isActionLoading}
      />

      <DeleteEventModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={async () => {
          setIsActionLoading(true);
          try {
            const response = await eventService.deleteEvent(event_slug);
            if (response.status) {
              toast.success(response.message || "Event deleted successfully");
              setIsDeleteModalOpen(false);
              navigate("/admin/media/events");
            } else {
              toast.error(response.message || "Failed to delete event");
            }
          } catch (err) {
            toast.error(err.message || "An error occurred");
          } finally {
            setIsActionLoading(false);
          }
        }}
        eventName={eventData.event_title || eventData.venue_name}
        isLoading={isActionLoading}
      />

      <MarkAsClosedModal
        isOpen={isMarkAsClosedModalOpen}
        onClose={() => setIsMarkAsClosedModalOpen(false)}
        onConfirm={async () => {
          setIsActionLoading(true);
          try {
            const response = await eventService.markAsClosed(event_slug);
            if (response.status) {
              toast.success(response.message || "Event marked as closed");
              setIsMarkAsClosedModalOpen(false);
              // Refresh event data
              const eventResponse = await eventService.getEvent(event_slug);
              if (eventResponse.status && eventResponse.data) {
                let event = null;
                if (
                  eventResponse.data.status_code === 200 &&
                  eventResponse.data.data
                ) {
                  event = eventResponse.data.data;
                } else if (
                  eventResponse.data &&
                  !eventResponse.data.status_code
                ) {
                  event = eventResponse.data;
                }
                if (event) setEventData(event);
              }
            } else {
              toast.error(response.message || "Failed to mark event as closed");
            }
          } catch (err) {
            toast.error(err.message || "An error occurred");
          } finally {
            setIsActionLoading(false);
          }
        }}
        eventName={eventData.event_title || eventData.venue_name}
        isLoading={isActionLoading}
      />
    </div>
  );
};

export default EventDetails;
